import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  Document,
  supportedFileTypes,
  withPreviewFileTypes,
} from '@sc/data-access-models';
import { LocaleDatePipe, TranslatorService } from '@sc/data-access-translation';
import { ScreenUtils } from '@sc/utils';
import { switchMap } from 'rxjs/operators';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';
import {
  MAX_OCTET_FILE,
  compressFile,
  isValidFileType,
  loadFile,
} from './input-picture.utils';

@Component({
  selector: 'sc-input-picture',
  styleUrls: ['./input-picture.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    IconComponent,
    NgClass,
    ButtonComponent,
    AsyncPipe,
    TranslateModule,
    LocaleDatePipe,
  ],
  template: `
    <div class="header">
      <div class="sc-design-text-2 label" [class.asterisk]="isRequired">
        {{ this.getPictureLabel(currentPicture) }}
      </div>
      <div
        *ngIf="!readOnly && currentPicture.url"
        class="sc-design-text-4-bold new-picture-button"
        [class.focus-highlight]="inputFocused"
        (click)="onNewPictureClick()"
      >
        <sc-icon
          name="copy"
          aria-hidden="true"
          specificName="input_picture_copy"
        ></sc-icon>
        <div class="button-label">
          {{ 'InputPicture.dropNewPicLabel' | translate }}
        </div>
        <input
          #newFileInput
          type="file"
          [accept]="mimeTypeList.join(',')"
          class="new-file-input"
          tabindex="0"
          (change)="onFileChange($event)"
          (focus)="inputFocused = true"
          (blur)="inputFocused = false"
        />
      </div>
    </div>
    <div class="body">
      <div
        *ngIf="!currentPicture.url && !readOnly"
        class="import-area"
        [ngClass]="{
          'is-dragover': isInputDragover,
          focused: inputFocused,
          error: error
        }"
        (dragover)="isInputDragover = true"
        (dragleave)="isInputDragover = false"
      >
        <input
          type="file"
          [accept]="mimeTypeList.join(',')"
          (change)="onFileChange($event)"
          (focus)="inputFocused = true"
          (blur)="inputFocused = false"
        />

        <sc-icon
          *ngIf="!error"
          class="picture_preview-icon"
          name="picture_preview"
        ></sc-icon>

        <div *ngIf="error" class="sc-design-text-3 error">
          {{
            (isTouchScreen()
              ? 'InputPicture.picErrorMobile'
              : 'InputPicture.picError'
            ) | translate
          }}
        </div>

        <div
          class="sc-design-link label"
          [innerHTML]="
            (isTouchScreen()
              ? 'InputPicture.dragNdropPicLabelMobile'
              : 'InputPicture.dragNdropPicLabel'
            ) | translate
          "
        ></div>

        <div
          class="sc-design-text-4 sub-label"
          [innerHTML]="'InputPicture.acceptedFormats' | translate"
        ></div>
      </div>

      <div
        class="empty-picture"
        *ngIf="!currentPicture.url && !error && readOnly"
      >
        <sc-icon class="picture_preview-icon" name="picture_preview"></sc-icon>

        <div class="label">
          {{ 'InputPicture.missingPicLabel' | translate }}
        </div>
      </div>
      <div class="preview-area" *ngIf="currentPicture.url && !error">
        <div class="infos">
          <div
            class="picture-preview"
            [class.no-preview]="!canPreview() || isImageError"
          >
            <img
              [src]="currentPicture.url"
              *ngIf="canPreview()"
              [alt]="currentPicture.metadata?.name || currentPicture.url"
              (error)="isImageError = true"
            />
            <sc-icon
              *ngIf="!canPreview()"
              class="no-preview-icon"
              name="picture_preview"
            ></sc-icon>
            <sc-icon
              class="check-icon"
              name="check"
              specificName="upload_page_check_icon"
              *ngIf="currentPicture.metadata?.name"
            ></sc-icon>
          </div>
          <div class="picture-props">
            <div class="sc-design-text-3">
              {{ currentPicture.metadata.name }}
            </div>
            <div class="sc-design-text-4 picture-date">
              {{
                currentPicture.metadata.lastModifiedDate
                  | localeDate: 'longDate'
                  | async
              }}
            </div>
          </div>
        </div>

        <div class="actions">
          <div class="mobile-picture-props">
            <div class="sc-design-text-3">
              {{ currentPicture.metadata.name }}
            </div>
            <div class="sc-design-text-4 picture-date">
              {{
                currentPicture.metadata.lastModifiedDate
                  | localeDate: 'longDate'
                  | async
              }}
            </div>
          </div>
          <div class="button-container">
            <sc-button
              buttonType="mainWhite"
              leftIcon="eye"
              (click)="onShowPictureClick()"
            >
              {{ 'InputPicture.see' | translate }}
            </sc-button>
            <sc-button
              *ngIf="!readOnly"
              leftIcon="delete"
              buttonType="errorWhite"
              (click)="onRemovePictureClick()"
              >{{ 'InputPicture.removeFile' | translate }}
            </sc-button>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class InputPictureComponent implements OnInit {
  @Input() picture: Document;
  @Input() readOnly: boolean;
  @Input() isRequired = false;
  @Output() showPicture = new EventEmitter<void>();
  @Output() updatePicture = new EventEmitter<Document>();

  @ViewChild('newFileInput') newFileInput: ElementRef<HTMLElement>;

  mimeTypeList = supportedFileTypes;
  isInputDragover: boolean;
  error = false;
  currentPicture: Document;

  inputFocused = false;
  isImageError = false;

  constructor(
    private readonly translator: TranslatorService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.currentPicture = { ...this.picture };
  }

  canPreview() {
    return withPreviewFileTypes.includes(
      this.currentPicture.metadata?.mimeType || this.currentPicture.file?.type
    );
  }

  getPictureLabel(picture: Document) {
    return this.translator.getInstantTranslation(
      `GlobalApplicativeConstants.documentType.${picture.type}`
    );
  }

  isTouchScreen(): boolean {
    return ScreenUtils.isTouchScreen();
  }

  onFileChange(event) {
    const newFile = event.target.files[0] as File & { lastModifiedDate?: Date };
    this.isInputDragover = false;
    this.currentPicture.name = null;

    if (newFile && isValidFileType(newFile, this.mimeTypeList)) {
      this.error = false;

      compressFile(newFile)
        .pipe(
          switchMap((compressedFile) => {
            if (compressedFile.size > MAX_OCTET_FILE) {
              throw new Error('File exceeds max size : ' + compressedFile.size);
            }

            this.currentPicture.file = compressedFile;
            return loadFile(this.currentPicture.file);
          })
        )
        .subscribe(
          (fileUrl) => {
            this.currentPicture.url = fileUrl;
            this.currentPicture.metadata = {
              lastModifiedDate: this.currentPicture.file.lastModifiedDate,
              name: this.currentPicture.file.name,
              mimeType: this.currentPicture.file.type,
            };
            this.updatePicture.emit(this.currentPicture);
            this.changeDetectorRef.detectChanges();
          },
          (error) => {
            console.error(error);
            this.uploadDocumentError();
          }
        );
    } else {
      console.error(new Error('Invalid file'), newFile);
      this.uploadDocumentError();
    }
  }

  uploadDocumentError() {
    this.error = true;
    this.currentPicture.metadata = null;
    this.currentPicture.file = null;
    this.currentPicture.url = null;
  }

  onNewPictureClick() {
    const nativeElement: HTMLElement = this.newFileInput.nativeElement;
    nativeElement.click();
  }

  onShowPictureClick() {
    this.showPicture.emit();
  }

  onRemovePictureClick() {
    this.updatePicture.emit({
      type: this.currentPicture.type,
      name: null,
      metadata: { lastModifiedDate: null, name: null, mimeType: null },
    });
  }
}
